import {
	RESOURCES_NAV_ITEMS,
	HELP_NAV_ITEMS,
	TOP_NAV_ITEMS,
	FOOTER_ITEMS,
	MESSAGE_CENTER,
	MEMBER_MANAGEMENT_NAV_ITEMS,
	BILLING_NAV_ITEMS,
	MY_ACCOUNT_PROFILE_NAV_ITEMS,
	LOGOUT_URL,
} from './navigationItems';
import { pageKey, retrievePermissionWithKey } from './pageConstants';

export const getCurrentPermission = (permissionName, permissions) => {
	const pagePermission = permissions.filter(
		permission => permission === permissionName
	);
	return pagePermission.length > 0 ? pagePermission[0] : null;
};

export const getRedirectionLink = (page, redirectionLinks) => {
	const legacyLink =
		redirectionLinks.filter(link => link.pageKey === page)[0]
			?.redirectUrl || null;

	return legacyLink;
};

export const updateNavsRedirectionPath = (navs, redirectionLinks) => {
	navs.forEach(nav => {
		const legacyLink = getRedirectionLink(nav.key, redirectionLinks);
		if (legacyLink) {
			nav.path = legacyLink;
			nav.isLegacy = true;
		}
	});
};

const getAccessibleDropdownNavs = (
	unfilteredNavItems,
	userSessionPermissions
) =>
	unfilteredNavItems.filter(navItem => {
		const pagePermission = retrievePermissionWithKey[navItem.key];
		if (Array.isArray(pagePermission)) {
			let userHasPermission = false;
			pagePermission.forEach(permission => {
				if (userSessionPermissions.includes(permission)) {
					userHasPermission = true;
				}
			});
			return userHasPermission;
		}
		return userSessionPermissions.includes(pagePermission);
	});

const sortNavigationItems = navs => navs.sort((a, b) => a.order - b.order);

const sortNavigationItemsByLabel = navs =>
	navs.sort((a, b) => a.label.localeCompare(b.label));

export const findPermissionInPermissions = (permission, permissions) =>
	permissions.includes(permission);

export const getResourcesNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(RESOURCES_NAV_ITEMS, permissions);
	updateNavsRedirectionPath(navs, redirectionLinks);

	sortNavigationItemsByLabel(navs);

	return navs;
};

export const getHelpNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(HELP_NAV_ITEMS, permissions);
	updateNavsRedirectionPath(navs, redirectionLinks);

	sortNavigationItems(navs);

	return navs;
};

export const getMemberManagementNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(
		MEMBER_MANAGEMENT_NAV_ITEMS,
		permissions
	);
	updateNavsRedirectionPath(navs, redirectionLinks);

	sortNavigationItems(navs);

	return navs;
};

// We combine the logic for the my account left nav for my account and the profile menu in the header here because they should always have the same menu items
export const getMyAccountProfileNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(
		MY_ACCOUNT_PROFILE_NAV_ITEMS,
		permissions
	);

	updateNavsRedirectionPath(navs, redirectionLinks);

	// The log out item is handled as a special case since it isn't associated with a permission and doesn't have a feature flag
	navs.push({
		key: pageKey.logOut,
		path: LOGOUT_URL,
		label: 'Log Out',
		order: 7,
		isLegacy: true,
	});

	sortNavigationItems(navs);

	return navs;
};

export const getBillingAndPaymentNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(BILLING_NAV_ITEMS, permissions);
	if (
		retrievePermissionWithKey[pageKey.claimsAndPayments].some(perm =>
			findPermissionInPermissions(perm, permissions)
		)
	) {
		navs.push(
			BILLING_NAV_ITEMS.find(
				item => item.key === pageKey.claimsAndPayments
			)
		);
	}
	updateNavsRedirectionPath(navs, redirectionLinks);

	sortNavigationItems(navs);

	return navs;
};

export const getMessageCenterPath = redirectionLinks => {
	updateNavsRedirectionPath([MESSAGE_CENTER], redirectionLinks);
	return MESSAGE_CENTER.path;
};

export const getFooterNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(FOOTER_ITEMS, permissions);
	updateNavsRedirectionPath(navs, redirectionLinks);

	return navs;
};

// Some items in the navigation bar in the header have a menu function rather in addition to being a link button
// this function makes clicking the item function the same as clicking the first item in the menu
const headerNavsHelper = (navs, items, navKey, redirectionLinks) => {
	if (items && items.length > 0) {
		const topNav = TOP_NAV_ITEMS.filter(
			navItem => navItem.key === navKey
		)[0];
		topNav.menuItems = items;
		const redirectLink = getRedirectionLink(items[0].key, redirectionLinks);
		topNav.path = redirectLink || items[0].path;
		navs.push(topNav);
	}
};

export const getHeaderNavs = (permissions, redirectionLinks) => {
	const navs = getAccessibleDropdownNavs(TOP_NAV_ITEMS, permissions);

	headerNavsHelper(
		navs,
		getResourcesNavs(permissions, redirectionLinks),
		'resources',
		redirectionLinks
	);

	headerNavsHelper(
		navs,
		getHelpNavs(permissions, redirectionLinks),
		'help',
		redirectionLinks
	);

	headerNavsHelper(
		navs,
		getMemberManagementNavs(permissions, redirectionLinks),
		'member-management',
		redirectionLinks
	);

	headerNavsHelper(
		navs,
		getBillingAndPaymentNavs(permissions, redirectionLinks),
		'billing-and-payments',
		redirectionLinks
	);

	updateNavsRedirectionPath(navs, redirectionLinks);

	sortNavigationItems(navs);
	return navs;
};

export const getHomePagePath = (permissions, redirectionLinks) => {
	const homePath = getHeaderNavs(permissions, redirectionLinks).filter(
		item => item.key === pageKey.home
	)[0]?.path;
	return homePath;
};

export const getMyAccountNavs = (permissions, redirectionLinks) =>
	getMyAccountProfileNavs(permissions, redirectionLinks);

export const getProfileNavs = (permissions, redirectionLinks) =>
	getMyAccountProfileNavs(permissions, redirectionLinks);
